import React, { useEffect, useRef } from 'react';
import { Box, Checkbox, Grid, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Shortcut, ShortcutAction, ShortcutGroup, ShortcutGroupToShortcuts, ShortcutGroupAction } from 'modules/shortcuts/models';
import { MAX_SHORTCUT_GROUP_NAME_WIDTH, TOTAL_NUM_OF_COLUMNS_SHORTCUTS } from 'modules/shortcuts/styles/constants';
import { MoreShortcutGroupOptions } from './moreShortcutGroupOptions';
import { ShortcutRow } from './shortcutRow';
import CollapsableTableRow from 'modules/common/components/collapsibleTableRow/collapsibleTableRow';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';
import { SET_SCROLL_TO_ROW_ID } from 'modules/shortcuts/reducer';

interface IShortcutsGroupRowProps {
    shortcutGroupToShortcuts: ShortcutGroupToShortcuts;
    selectedShortcuts: Shortcut[];
    onSelectShortcut: (selected: boolean, shortcut: Shortcut) => void;
    onSelectGroup: (selected: boolean, shortcuts: ShortcutGroup) => void;
    onToggleExpand: (expanded: boolean, group: ShortcutGroup) => void
    expanded: boolean;
    onShortcutAction: (shortcut: Shortcut, shortcutAction: ShortcutAction) => void;
    onShortcutGroupAction: (shortcutGroup: ShortcutGroup, shortcutGroupAction: ShortcutGroupAction) => void;
    selectedShortcutGroups: ShortcutGroup[];
}

export const ShortcutsGroupRow: React.FC<IShortcutsGroupRowProps> = ({
    shortcutGroupToShortcuts,
    selectedShortcuts,
    onSelectShortcut,
    onSelectGroup,
    onToggleExpand,
    expanded,
    onShortcutAction,
    onShortcutGroupAction,
    selectedShortcutGroups
}) => {
    const shortcutGroup = shortcutGroupToShortcuts.shortcutGroup;
    const shortcutsInGroup = shortcutGroupToShortcuts.shortcuts;
    const isShortcutGroupSelected = selectedShortcutGroups.find(sg => sg.id === shortcutGroup.id) !== undefined;
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const { isFetching, scrollToRowId } = useSelector((state: GlobalApplicationState) => state.shortcuts);
    const dispatch = useDispatch();

    const toggleExpand = () => onToggleExpand(!expanded, shortcutGroup);

    const onChangeCheck = () => {
        const isSelected = !isShortcutGroupSelected;
        
        onSelectGroup(isSelected, shortcutGroup);
    };

    useEffect(() => {
        if (scrollToRowId && !isFetching) {
            const rowRef = rowRefs.current[scrollToRowId];
            if (rowRef) {
                rowRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            dispatch(SET_SCROLL_TO_ROW_ID());
        }
    }, [scrollToRowId, isFetching])

    const getEmptyShortcutGroup = (): JSX.Element =>
        <CollapsableTableRow
            rowKey={`empty-shortcut-group-${shortcutGroup.id}`}
            expanded={expanded} 
            tableCellElements={[
                { element: undefined }, 
                {
                    element: <Typography color="grey" fontSize="14px">
                        <i> No shortcuts in shortcut group </i>
                    </Typography>
                }
            ]}
        />;

    const getRowsInGroup = () => shortcutsInGroup.length !== 0 
        ? shortcutsInGroup.map((shortcut) =>
            <ShortcutRow
                shortcut={shortcut}
                selected={selectedShortcuts.map(s => s.id).includes(shortcut.id)}
                onSelectShortcut={onSelectShortcut}
                onShortcutAction={onShortcutAction}
                key={shortcut.id}
                expanded={expanded}
                rowRefs={rowRefs}
            />
        ) 
        : getEmptyShortcutGroup();

    return <React.Fragment key={`${shortcutGroup.id}-frag`}>
        <TableRow
            hover
            key={shortcutGroup.id}
            role="checkbox"
            tabIndex={-1}
            aria-checked={isShortcutGroupSelected}
            selected={isShortcutGroupSelected}
            ref={ref => rowRefs.current[shortcutGroup.id] = ref}
        >
            <TableCell colSpan={2}>
                <Grid
                    container
                    wrap="nowrap"
                    alignItems="center"
                >
                    <Grid item padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isShortcutGroupSelected}
                            onClick={onChangeCheck}
                        />
                    </Grid>
                    <Grid item onClick={toggleExpand}>
                        <Box 
                            maxWidth={MAX_SHORTCUT_GROUP_NAME_WIDTH} 
                            justifyContent={"start"}
                        >
                            <Typography
                                color="primary"
                                fontWeight="500"
                                textTransform={"uppercase"}
                                sx={{ WebkitLineClamp: 2 }}
                                fontSize="inherit"
                                className="shortcut-group-name-truncated"
                            >
                                {shortcutGroup.name}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </TableCell>
            <TableCell colSpan={TOTAL_NUM_OF_COLUMNS_SHORTCUTS - 3} onClick={toggleExpand}>
                <MoreShortcutGroupOptions
                    shortcutGroupToShortcuts={shortcutGroupToShortcuts}
                    onShortcutGroupAction={a => onShortcutGroupAction(shortcutGroup, a)}
                />
            </TableCell>
            <TableCell align="right">
                <IconButton size="small" onClick={toggleExpand}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
            </TableCell>
        </TableRow>
        {getRowsInGroup()}
    </React.Fragment>;
};